/**
 * Pure helpers for the event's Transport block, kept out of the tab so the
 * rules (None clears the acknowledgement, a blank text falls back to a default
 * naming the method) can be tested without React.
 */

export const NO_TRANSPORT_CODE = "none";

export interface TransportDoc {
	Code: string;
	Notes: string | null;
	AcknowledgementRequired: boolean;
	AcknowledgementText: string | null;
}

export interface TransportChange {
	code?: string;
	notes?: string;
	acknowledgementRequired?: boolean;
	acknowledgementText?: string;
}

/** The text carers are asked to acknowledge when the organiser leaves it blank. */
export function defaultAckText(methodLabel: string): string {
	return `I acknowledge my daughter will be travelling via ${methodLabel.toLowerCase()}`;
}

/**
 * Apply one edit to the stored block. Returns null (clear) when the result is
 * None, with no notes and no acknowledgement. The acknowledgement text is kept
 * as typed here; `finalizeTransport` fills the default at save time.
 */
export function buildTransportValue(
	current: TransportDoc | null | undefined,
	change: TransportChange,
): TransportDoc | null {
	const code = change.code ?? current?.Code ?? NO_TRANSPORT_CODE;
	const notes = change.notes ?? current?.Notes ?? "";
	let required =
		change.acknowledgementRequired ?? current?.AcknowledgementRequired === true;
	let text = change.acknowledgementText ?? current?.AcknowledgementText ?? "";
	// None has nothing to acknowledge.
	if (code === NO_TRANSPORT_CODE) {
		required = false;
		text = "";
	}
	if (code === NO_TRANSPORT_CODE && !notes.trim() && !required) return null;
	return {
		Code: code,
		Notes: notes,
		AcknowledgementRequired: required,
		AcknowledgementText: text,
	};
}

/**
 * The block as sent on save: notes and text trimmed to null when blank, and a
 * required acknowledgement with no text given the default for its method.
 * `undefined` and `null` pass through, so an untouched event stays untouched.
 */
export function finalizeTransport(
	transport: TransportDoc | null | undefined,
	labelFor: (code: string) => string,
): TransportDoc | null | undefined {
	if (!transport) return transport;
	const required = transport.AcknowledgementRequired === true;
	const typed = (transport.AcknowledgementText ?? "").trim();
	return {
		Code: transport.Code,
		Notes: transport.Notes ?? null,
		AcknowledgementRequired: required,
		AcknowledgementText: required
			? typed || defaultAckText(labelFor(transport.Code))
			: typed || null,
	};
}

/**
 * Only the four keys the API accepts. The read adds `Label` and `Description`
 * from the dictionary, and the write model refuses unknown keys, so a block
 * sent back as it was read (a calendar drag, a bulk tag) would be rejected.
 */
export function pickTransport(value: unknown): unknown {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return value;
	}
	const t = value as Record<string, unknown>;
	return {
		Code: t.Code,
		Notes: t.Notes ?? null,
		AcknowledgementRequired: t.AcknowledgementRequired === true,
		AcknowledgementText: t.AcknowledgementText ?? null,
	};
}
